import { useSearchParams } from 'react-router-dom';
import {
  TASK_SORT_PARAMS,
  type TaskSortSelection,
  type TaskTabSort,
  buildTaskSortBy,
  isTaskSortOrder,
} from './taskSort';
import { useWriteTaskParams } from './useWriteTaskParams';

/**
 * Owns a Tasks-page tab's sort, kept in the URL like its filters, so each tab
 * sorts independently and refresh or back/forward keeps the order.
 */
export const useTaskSort = (sort: TaskTabSort | undefined) => {
  const [searchParams] = useSearchParams();
  const writeParams = useWriteTaskParams();
  const fieldParam = searchParams.get(TASK_SORT_PARAMS.field);
  const orderParam = searchParams.get(TASK_SORT_PARAMS.order);

  const selection: TaskSortSelection | undefined = sort && {
    field:
      sort.fields.find((field) => field === fieldParam) ?? sort.default.field,
    order: isTaskSortOrder(orderParam) ? orderParam : sort.default.order,
  };

  const setSelection = ({ field, order }: TaskSortSelection) => {
    // Re-picking the current option would otherwise still reset the page.
    if (field === selection?.field && order === selection?.order) return;

    // Defaults stay out of the URL, so a shared link only carries a deliberate choice.
    writeParams({
      [TASK_SORT_PARAMS.field]: field === sort?.default.field ? '' : field,
      [TASK_SORT_PARAMS.order]: order === sort?.default.order ? '' : order,
    });
  };

  return {
    selection,
    setSelection,
    sortBy: sort && selection && buildTaskSortBy(sort, selection),
  };
};

export type TaskSort = ReturnType<typeof useTaskSort>;
